import { Box, CssBaseline, Toolbar } from "@mui/material";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthProvider } from "./contexts/AuthContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import { ThemeProvider } from "./contexts/ThemeContext";

// Components
import AuthRedirect from "./components/common/AuthRedirect";
import Navbar from "./components/common/Navbar";
import ProtectedRoute from "./components/common/ProtectedRoute";

// Pages
import DashboardPage from "./pages/DashboardPage";
import DownloadPage from "./pages/DownloadPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import HardwareIdManagementPage from "./pages/HardwareIdManagementPage";
import LoginPage from "./pages/LoginPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import ProductListPage from "./pages/ProductListPage";
import RegisterPage from "./pages/RegisterPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ResourceDownloadPage from "./pages/ResourceDownloadPage";
import ResourcesPage from "./pages/ResourcesPage";
import UnauthorizedPage from "./pages/UnauthorizedPage";

// Admin Pages
import AdminDashboardPage from "./pages/admin/AdminDashboardPage";
import DownloadStatisticsPage from "./pages/admin/DownloadStatisticsPage";
import HardwareSubmissionPage from "./pages/admin/HardwareSubmissionPage";
import ProductManagementPage from "./pages/admin/ProductManagementPage";
import ProductVersionsPage from "./pages/admin/ProductVersionsPage";
import ResourceManagementPage from "./pages/admin/ResourceManagementPage";
import SessionViewerPage from "./pages/admin/SessionViewerPage";
import UserManagementPage from "./pages/admin/UserManagementPage";

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <NotificationProvider>
          <CssBaseline />
          <Router>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                minHeight: "100vh",
                width: "100%",
                overflow: "hidden",
              }}
            >
              <Navbar />
              <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }} />{" "}
              {/* This empty Toolbar acts as spacer for fixed AppBar */}
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  overflow: "hidden",
                  px: 0,
                }}
              >
                <Routes>
                  {/* Public routes */}
                  <Route path="/login" element={<LoginPage />} />

                  <Route
                    path="/forgot-password"
                    element={<ForgotPasswordPage />}
                  />
                  <Route
                    path="/reset-password"
                    element={<ResetPasswordPage />}
                  />
                  <Route path="/unauthorized" element={<UnauthorizedPage />} />

                  {/* Protected routes */}
                  <Route element={<ProtectedRoute />}>
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/products" element={<ProductListPage />} />
                    <Route
                      path="/products/:productId"
                      element={<ProductDetailPage />}
                    />
                    <Route
                      path="/download/:versionId"
                      element={<DownloadPage />}
                    />
                    <Route path="/resources" element={<ResourcesPage />} />
                    <Route
                      path="/resources/download/:resourceId"
                      element={<ResourceDownloadPage />}
                    />
                    <Route
                      path="/hardware-management"
                      element={<HardwareIdManagementPage />}
                    />
                  </Route>

                  {/* Admin routes */}
                  <Route element={<ProtectedRoute requiredRole="admin" />}>
                    <Route path="/admin/register" element={<RegisterPage />} />
                    <Route
                      path="/admin/dashboard"
                      element={<AdminDashboardPage />}
                    />
                    <Route
                      path="/admin/products"
                      element={<ProductManagementPage />}
                    />
                    <Route
                      path="/admin/products/:productId/versions"
                      element={<ProductVersionsPage />}
                    />
                    <Route
                      path="/admin/users"
                      element={<UserManagementPage />}
                    />
                    <Route
                      path="/admin/statistics"
                      element={<DownloadStatisticsPage />}
                    />
                    <Route
                      path="/admin/sessions"
                      element={<SessionViewerPage />}
                    />
                    <Route
                      path="/admin/resources"
                      element={<ResourceManagementPage />}
                    />
                    <Route
                      path="/admin/hardware-submissions"
                      element={<HardwareSubmissionPage />}
                    />
                  </Route>

                  {/* Redirect based on authentication status */}
                  <Route
                    path="/"
                    element={
                      <AuthRedirect
                        loginPath="/login"
                        userPath="/dashboard"
                        adminPath="/admin/dashboard"
                      />
                    }
                  />
                  <Route path="*" element={<Navigate to="/" />} />
                </Routes>
              </Box>
              <ToastContainer
                position="bottom-right"
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                style={{ zIndex: 9999 }}
              />
            </Box>
          </Router>
        </NotificationProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
